import { useEffect, useMemo, useState } from "react";
import api, { formatErr } from "../../lib/api";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Clock } from "lucide-react";

const pad = (n) => (n < 10 ? `0${n}` : `${n}`);

export default function Hours({ projectId, year, month, onCellClick }) {
  const [data, setData] = useState(null);

  const load = async () => {
    if (!projectId) return;
    try {
      const r = await api.get(`/hours/project/${projectId}`, {
        params: { year, month },
      });
      setData(r.data);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail));
    }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [projectId, year, month]);

  const dateList = useMemo(() => {
    const days = data?.days_in_month || 0;
    return Array.from({ length: days }, (_, i) =>
      `${year}-${pad(month)}-${pad(i + 1)}`
    );
  }, [data, year, month]);

  const limit = data?.daily_hour_limit ?? 8;

  return (
    <div className="space-y-6" data-testid="hours-page">

      <Card className="p-4">
        <div className="text-sm font-semibold text-[#1A3A5C] mb-2">Legend</div>
        <div className="flex flex-wrap gap-3 text-xs text-[#475569] items-center">
          <span className="flex items-center gap-1.5">
            <span className="inline-flex items-center justify-center w-9 h-7 rounded border bg-[#22C55E]/15 text-[#15803D] border-[#22C55E]/30 text-[11px] font-bold">
              7.5
            </span>{" "}
            Hours worked
          </span>
          <span className="flex items-center gap-1.5">
            <span className="inline-flex items-center justify-center w-9 h-7 rounded border bg-[#F0A830] text-white border-[#D89220] text-[11px] font-bold">
              9.5
            </span>{" "}
            Over limit
          </span>
          <span className="flex items-center gap-1.5">
            <span className="inline-flex items-center justify-center w-9 h-7 rounded border bg-[#FEE2E2] text-[#B91C1C] border-[#FCA5A5] text-[11px] font-bold">
              A
            </span>{" "}
            Absent
          </span>
          <span className="flex items-center gap-1.5">
            <span className="inline-flex items-center justify-center w-9 h-7 rounded border bg-[#A855F7] text-white border-[#7E22CE] text-[11px] font-bold">
              H
            </span>{" "}
            Holiday
          </span>
        </div>
        <p className="text-xs text-[#475569] mt-3">
          Daily limit:{" "}
          <strong className="text-[#1A3A5C]">{limit} hrs</strong> (edit on
          Projects page).
        </p>
      </Card>

      {!data ? (
        <Card className="p-8 text-center text-[#475569]">
          {!projectId ? "Select a project first." : "Loading…"}
        </Card>
      ) : data.workers.length === 0 ? (
        <Card className="p-8 text-center text-[#475569]">
          No employees assigned to this project yet.
        </Card>
      ) : (
        <Card className="p-0 overflow-hidden">
          <table className="w-full border-collapse table-fixed text-xs">
            <colgroup>
              <col style={{ width: "120px" }} />
              <col style={{ width: "60px" }} />
              {dateList.map((d) => (
                <col key={d} />
              ))}
            </colgroup>
            <thead className="bg-slate-50">
              <tr>
                <th className="px-2 py-2 text-left text-xs font-semibold text-[#475569] sticky left-0 bg-slate-50 z-10">
                  Employee
                </th>
                <th className="px-1 py-2 text-[10px] font-semibold text-[#475569] bg-[#1A3A5C]/5">
                  Total
                </th>
                {dateList.map((d) => {
                  const day = parseInt(d.split("-")[2]);
                  return (
                    <th
                      key={d}
                      className="px-0 py-2 text-[10px] font-semibold text-[#475569]"
                    >
                      {day}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {data.workers.map((w) => (
                <tr
                  key={w.id}
                  data-testid={`hours-row-${w.worker_id}`}
                  className="border-t hover:bg-slate-50"
                >
                  <td className="px-2 py-1.5 sticky left-0 bg-white z-10 border-r">
                    <div className="font-semibold text-[#1A3A5C] text-xs truncate">
                      {w.name}
                    </div>
                    <div className="text-[10px] text-[#475569] font-mono">
                      {w.worker_id}
                    </div>
                  </td>
                  <td className="px-1 py-1.5 text-center">
                    <span className="inline-block bg-[#1A3A5C] text-white text-[10px] font-bold rounded-full px-2 py-0.5">
                      {data.totals[w.id]?.toFixed?.(1) || "0.0"}
                    </span>
                  </td>
                  {dateList.map((d) => {
                    const cell = data.cells?.[w.id]?.[d];
                    const isHol = data.holidays?.includes(d);
                    let content = "·";
                    let cls = "hour-cell";
                    let title = "";
                    if (cell?.status === "absent") {
                      content = "A";
                      cls = "hour-cell absent";
                      title = "Absent";
                    } else if (cell?.status === "present") {
                      const hrs = cell.hours;
                      if (hrs == null) {
                        content = "P";
                        cls = "hour-cell present";
                        title = `In: ${cell.in || "-"} (not checked out)`;
                      } else {
                        const over = hrs > limit;
                        content = hrs.toFixed(1);
                        cls = over
                          ? "hour-cell over-limit"
                          : "hour-cell present";
                        title = `In: ${cell.in || "-"} · Out: ${cell.out || "-"}`;
                      }
                    } else if (isHol) {
                      content = "H";
                      cls = "hour-cell holiday";
                      title = "Holiday";
                    }
                    return (
                      <td key={d} className="px-0 py-1 text-center">
                        <span
                          className={`${cls} clickable`}
                          title={title}
                          onClick={() => onCellClick && onCellClick(w, d)}
                        >
                          {content}
                        </span>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}
